import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  Copy,
  Hash,
  Package,
  Search,
  Tag,
  XCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { PageContainer } from '@/components/common/PageContainer'
import { AppButton } from '@/components/rfid/AppButton'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { BRAND } from '@/constants/brand'
import { useLocale } from '@/i18n/LocaleProvider'
import {
  engineService,
  type BatchLookupResult,
  type EngineTagRecord,
  type EngineTagStatus,
  type SkuLookupResult,
} from '@/services/engine.service'
import { cn } from '@/utils/cn'
import {
  formatEngineCountry,
  formatEngineDateTime,
  formatEngineMonthYear,
  formatEngineSerial,
  formatEngineStatus,
} from '@/utils/rfidEncodingDisplay'

type Mode = 'rfid' | 'sku' | 'batch'

function StatusBadge({ status }: { status: EngineTagStatus }) {
  const tagged = status === 'tagged'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-lg px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide',
        tagged
          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
          : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200'
      )}
    >
      {formatEngineStatus(status)}
    </span>
  )
}

function MetaCell({
  label,
  value,
  secondary,
}: {
  label: string
  value: string
  secondary?: string
}) {
  return (
    <div className="rounded-xl border border-border bg-muted/40 px-3 py-2.5">
      <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 break-words text-[13px] font-extrabold leading-snug text-foreground">
        {value || '—'}
      </div>
      {secondary ? (
        <div className="mt-0.5 font-mono text-[10px] font-semibold text-muted-foreground">
          {secondary}
        </div>
      ) : null}
    </div>
  )
}

function copyText(value: string, okMsg: string) {
  void navigator.clipboard?.writeText(value).then(
    () => toast.success(okMsg),
    () => toast.error('Copy failed')
  )
}

function RfidResultCard({
  row,
  locale,
  labels,
}: {
  row: EngineTagRecord
  locale: 'en' | 'ar'
  labels: {
    sku: string
    unmapped: string
    status: string
    country: string
    monthYear: string
    serial: string
    birthedAt: string
    taggedAt: string
    copied: string
    copySku: string
  }
}) {
  const country = formatEngineCountry(row.country_code, locale)
  const monthYear = formatEngineMonthYear(row.month_year, locale)
  const serial = formatEngineSerial(row.serial)
  const sku = row.sku_code?.trim() || ''
  const hasSku = sku.length > 0

  return (
    <div className="overflow-hidden rounded-2xl border-[1.5px] border-border bg-card shadow-sm">
      {/* RFID header */}
      <div className="border-b border-border px-4 py-3.5" style={{ background: `${BRAND}08` }}>
        <div className="flex items-start gap-3">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px]"
            style={{ background: `${BRAND}14`, color: BRAND }}
          >
            <Tag className="h-5 w-5" strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              RFID
            </div>
            <div className="mt-0.5 break-all font-mono text-[14px] font-black leading-snug text-foreground">
              {row.rfid_code}
            </div>
            <div className="mt-2">
              <StatusBadge status={row.status} />
            </div>
          </div>
          <button
            type="button"
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Copy RFID"
            onClick={() => copyText(row.rfid_code, labels.copied)}
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Prominent SKU */}
      <div
        className={cn(
          'border-b border-border px-4 py-4',
          hasSku ? 'bg-card' : 'bg-amber-50/70'
        )}
      >
        <div className="flex items-start gap-3">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px]"
            style={{
              background: hasSku ? `${BRAND}14` : '#fde68a',
              color: hasSku ? BRAND : '#92400e',
            }}
          >
            <Package className="h-5 w-5" strokeWidth={2.25} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              {labels.sku}
            </div>
            <div
              className={cn(
                'mt-1 break-all font-mono text-[20px] font-black leading-tight tracking-tight',
                hasSku ? 'text-foreground' : 'text-amber-900'
              )}
            >
              {hasSku ? sku : labels.unmapped}
            </div>
          </div>
          {hasSku ? (
            <button
              type="button"
              className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
              title={labels.copySku}
              onClick={() => copyText(sku, labels.copied)}
            >
              <Copy className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Formatted metadata */}
      <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3">
        <MetaCell
          label={labels.country}
          value={country.primary}
          secondary={country.secondary}
        />
        <MetaCell
          label={labels.monthYear}
          value={monthYear.primary}
          secondary={monthYear.secondary}
        />
        <MetaCell label={labels.serial} value={serial} />
        <MetaCell
          label={labels.birthedAt}
          value={formatEngineDateTime(row.birthed_at, locale)}
        />
        <MetaCell
          label={labels.taggedAt}
          value={formatEngineDateTime(row.tagged_at, locale)}
        />
        <MetaCell
          label={labels.status}
          value={formatEngineStatus(row.status, locale)}
        />
      </div>
    </div>
  )
}

export default function EngineLookupPage() {
  const navigate = useNavigate()
  const { t, locale } = useLocale()
  const [mode, setMode] = useState<Mode>('rfid')
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [rfidHit, setRfidHit] = useState<EngineTagRecord | null>(null)
  const [skuHit, setSkuHit] = useState<SkuLookupResult | null>(null)
  const [batchHit, setBatchHit] = useState<BatchLookupResult | null>(null)
  const [listFilter, setListFilter] = useState('')

  const clearResults = () => {
    setRfidHit(null)
    setSkuHit(null)
    setBatchHit(null)
    setSearched(false)
    setListFilter('')
  }

  const switchMode = (next: Mode) => {
    setMode(next)
    setQuery('')
    clearResults()
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) {
      toast.error(t.Engine?.needQuery ?? 'Enter a value to look up')
      return
    }
    setLoading(true)
    clearResults()
    try {
      if (mode === 'rfid') {
        const data = await engineService.lookupByRfid(query)
        setRfidHit(data)
      } else if (mode === 'sku') {
        const data = await engineService.lookupBySku(query)
        setSkuHit(data)
      } else {
        const codes = engineService.splitCodes(query)
        if (codes.length === 0) {
          toast.error(t.Engine?.needRfid ?? 'Enter at least one RFID code')
          return
        }
        const data = await engineService.lookupBatch(codes)
        setBatchHit(data)
      }
      setSearched(true)
    } catch (err) {
      toast.error((err as { message?: string })?.message || 'Lookup failed')
      setSearched(true)
    } finally {
      setLoading(false)
    }
  }

  const modes = [
    {
      id: 'rfid' as const,
      label: t.Engine?.byRfid ?? 'By RFID',
      icon: Tag,
      hint: t.Engine?.lookupHintRfid ?? 'Look up one RFID code to see SKU mapping and birth metadata.',
      placeholder: t.Engine?.lookupPlaceholderRfid ?? 'e.g. 01A250000001',
    },
    {
      id: 'sku' as const,
      label: t.Engine?.bySku ?? 'By SKU',
      icon: Package,
      hint: t.Engine?.lookupHintSku ?? 'Find every RFID tagged to a SKU.',
      placeholder: t.Engine?.lookupPlaceholderSku ?? 'e.g. MAT-100028',
    },
    {
      id: 'batch' as const,
      label: t.Engine?.batch ?? 'Batch',
      icon: Hash,
      hint:
        t.Engine?.lookupHintBatch ??
        'Paste multiple RFID codes (space, comma, or newline separated).',
      placeholder:
        t.Engine?.lookupPlaceholderBatch ?? '01A250000001\n01A250000002\n01A250000003',
    },
  ]

  const activeMode = modes.find((m) => m.id === mode)!

  const filteredSkuCodes = useMemo(() => {
    if (!skuHit) return []
    const q = listFilter.trim().toLowerCase()
    if (!q) return skuHit.rfid_codes
    return skuHit.rfid_codes.filter((c) => c.toLowerCase().includes(q))
  }, [skuHit, listFilter])

  const filteredBatchFound = useMemo(() => {
    if (!batchHit) return []
    const q = listFilter.trim().toLowerCase()
    if (!q) return batchHit.found
    return batchHit.found.filter(
      (r) =>
        r.rfid_code.toLowerCase().includes(q) ||
        (r.sku_code ?? '').toLowerCase().includes(q)
    )
  }, [batchHit, listFilter])

  const filteredBatchMissing = useMemo(() => {
    if (!batchHit) return []
    const q = listFilter.trim().toLowerCase()
    if (!q) return batchHit.missing
    return batchHit.missing.filter((c) => c.toLowerCase().includes(q))
  }, [batchHit, listFilter])

  const hasResults = Boolean(rfidHit || skuHit || batchHit)
  const resultLabels = {
    sku: t.Engine?.skuCode ?? 'SKU',
    unmapped: t.Engine?.unmapped ?? 'Unmapped',
    status: t.Engine?.statusLabel ?? 'Status',
    country: t.Engine?.country ?? 'Country',
    monthYear: t.Engine?.monthYear ?? 'Month / year',
    serial: t.Engine?.serial ?? 'Serial',
    birthedAt: t.Engine?.birthedAt ?? 'Birthed',
    taggedAt: t.Engine?.taggedAt ?? 'Tagged',
    copied: t.Engine?.copied ?? 'Copied',
    copySku: t.Engine?.copySku ?? 'Copy SKU',
  }

  return (
    <PageContainer fluid className="!flex !h-full !min-h-0 !flex-col !px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title={t.Engine?.lookupTitle ?? 'Lookup'}
          subtitle={t.Engine?.lookupSubtitle ?? 'RFID → SKU and SKU → RFIDs'}
          onBack={() => navigate('/engine')}
        />

        <div className="rfid-wizard-body !flex !min-h-0 !flex-1 !flex-col !gap-4 !overflow-auto">
          {/* Mode switcher */}
          <div className="flex flex-wrap gap-2">
            {modes.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => switchMode(id)}
                className={cn(
                  'inline-flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-[13px] font-extrabold transition-colors',
                  mode === id
                    ? 'border-transparent text-white shadow-sm'
                    : 'border-border bg-card text-foreground hover:bg-muted/60'
                )}
                style={mode === id ? { background: BRAND } : undefined}
              >
                <Icon className="h-4 w-4" strokeWidth={2.25} />
                {label}
              </button>
            ))}
          </div>

          {/* Query card */}
          <form
            onSubmit={onSubmit}
            className="rounded-2xl border-[1.5px] border-border bg-card p-4 shadow-sm sm:p-5"
          >
            <p className="m-0 text-xs font-semibold leading-5 text-muted-foreground">
              {activeMode.hint}
            </p>

            <label className="mt-4 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              {mode === 'sku'
                ? t.Engine?.skuCode ?? 'SKU code'
                : t.Engine?.rfidCodes ?? 'RFID code(s)'}
            </label>

            {mode === 'batch' ? (
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                rows={5}
                placeholder={activeMode.placeholder}
                className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2.5 font-mono text-sm outline-none transition-shadow placeholder:text-muted-foreground/50 focus:border-[var(--leap-brand)] focus:shadow-[0_0_0_3px_rgba(175,0,0,0.12)]"
                data-no-wedge="true"
              />
            ) : (
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={activeMode.placeholder}
                className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2.5 font-mono text-sm outline-none transition-shadow placeholder:text-muted-foreground/50 focus:border-[var(--leap-brand)] focus:shadow-[0_0_0_3px_rgba(175,0,0,0.12)]"
                data-no-wedge="true"
                autoComplete="off"
                spellCheck={false}
              />
            )}

            {mode === 'batch' && query.trim() ? (
              <div className="mt-2 text-[11px] font-semibold text-muted-foreground">
                {(t.Engine?.batchCountHint ?? '{{count}} unique code(s)').replace(
                  '{{count}}',
                  String(engineService.splitCodes(query).length)
                )}
              </div>
            ) : null}

            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <AppButton type="submit" loading={loading} block className="sm:flex-1">
                <Search className="h-4 w-4" strokeWidth={2.5} />
                {t.Engine?.lookupAction ?? 'Look up'}
              </AppButton>
              {hasResults || searched ? (
                <AppButton
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setQuery('')
                    clearResults()
                  }}
                  className="sm:w-auto"
                >
                  {t.Engine?.clear ?? 'Clear'}
                </AppButton>
              ) : null}
            </div>
          </form>

          {/* Empty / idle state */}
          {!hasResults && !searched ? (
            <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/60 px-6 py-12 text-center">
              <span
                className="flex h-14 w-14 items-center justify-center rounded-2xl"
                style={{ background: `${BRAND}12`, color: BRAND }}
              >
                <Search className="h-7 w-7" strokeWidth={2} />
              </span>
              <div className="mt-4 text-[15px] font-black text-foreground">
                {t.Engine?.lookupEmptyTitle ?? 'Search the RFID engine'}
              </div>
              <p className="mt-2 max-w-md text-xs font-semibold leading-5 text-muted-foreground">
                {t.Engine?.lookupEmptyBody ??
                  'Resolve a single RFID, list every tag on a SKU, or batch-check codes against the birth / tag registry.'}
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {modes.map(({ id, label, icon: Icon }) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[11px] font-bold text-muted-foreground"
                  >
                    <Icon className="h-3.5 w-3.5" style={{ color: BRAND }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/* No hit after search */}
          {searched && !hasResults && !loading ? (
            <div className="flex flex-col items-center rounded-2xl border border-border bg-card px-6 py-10 text-center shadow-sm">
              <XCircle className="h-10 w-10 text-muted-foreground/70" strokeWidth={1.75} />
              <div className="mt-3 text-sm font-black text-foreground">
                {t.Engine?.lookupNoResults ?? 'No results'}
              </div>
              <p className="mt-1 text-xs font-semibold text-muted-foreground">
                {t.Engine?.lookupNoResultsHint ?? 'Check the code and try again.'}
              </p>
            </div>
          ) : null}

          {/* Single RFID result */}
          {rfidHit ? (
            <RfidResultCard row={rfidHit} locale={locale} labels={resultLabels} />
          ) : null}

          {/* SKU → RFIDs */}
          {skuHit ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              <div className="rounded-2xl border-[1.5px] border-border bg-card p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {t.Engine?.skuCode ?? 'SKU'}
                    </div>
                    <div className="mt-0.5 break-all font-mono text-[16px] font-black text-foreground">
                      {skuHit.sku_code}
                    </div>
                  </div>
                  <div
                    className="rounded-xl px-3 py-2 text-center"
                    style={{ background: `${BRAND}12` }}
                  >
                    <div className="text-2xl font-black tabular-nums leading-none" style={{ color: BRAND }}>
                      {skuHit.rfid_codes.length}
                    </div>
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {t.Engine?.tagsLabel ?? 'Tags'}
                    </div>
                  </div>
                </div>

                {skuHit.rfid_codes.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <AppButton
                      type="button"
                      variant="outline"
                      onClick={() =>
                        copyText(
                          skuHit.rfid_codes.join('\n'),
                          t.Engine?.copiedAll ?? 'All RFID codes copied'
                        )
                      }
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {t.Engine?.copyAll ?? 'Copy all'}
                    </AppButton>
                  </div>
                ) : null}
              </div>

              {skuHit.rfid_codes.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card px-4 py-8 text-center text-xs font-semibold text-muted-foreground">
                  {t.Engine?.noTagsForSku ?? 'No tagged RFIDs for this SKU'}
                </div>
              ) : (
                <>
                  {skuHit.rfid_codes.length > 6 ? (
                    <input
                      type="search"
                      className="rfid-search font-mono"
                      placeholder={t.Engine?.filterTags ?? 'Filter tags…'}
                      value={listFilter}
                      onChange={(e) => setListFilter(e.target.value)}
                      data-no-wedge="true"
                    />
                  ) : null}
                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-[14px] border border-border bg-card">
                    {filteredSkuCodes.length === 0 ? (
                      <p className="m-0 px-4 py-6 text-center text-xs font-semibold text-muted-foreground">
                        {t.Engine?.noSearchMatch ?? 'No tags match this search'}
                      </p>
                    ) : (
                      <ul className="m-0 list-none p-0">
                        {filteredSkuCodes.map((code, index) => (
                          <li
                            key={code}
                            className="flex items-center gap-2.5 px-3.5 py-3"
                            style={{
                              borderTop: index === 0 ? undefined : '1px solid var(--border)',
                            }}
                          >
                            <CheckCircle2
                              className="h-4 w-4 shrink-0 text-emerald-600"
                              strokeWidth={2.25}
                            />
                            <span className="min-w-0 flex-1 break-all font-mono text-[12px] font-bold text-foreground">
                              {code}
                            </span>
                            <button
                              type="button"
                              className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                              onClick={() => copyText(code, resultLabels.copied)}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : null}

          {/* Batch results */}
          {batchHit ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              <div
                className="flex shrink-0 items-center rounded-2xl px-4 py-4"
                style={{ backgroundColor: '#1E293B' }}
              >
                <div className="flex flex-1 flex-col items-center">
                  <div className="text-3xl font-black tabular-nums leading-none text-emerald-400">
                    {batchHit.found.length}
                  </div>
                  <div className="mt-1 text-[11px] font-bold text-white/60">
                    {t.Engine?.found ?? 'Found'}
                  </div>
                </div>
                <div className="h-10 w-px bg-white/20" />
                <div className="flex flex-1 flex-col items-center">
                  <div className="text-3xl font-black tabular-nums leading-none text-amber-300">
                    {batchHit.missing.length}
                  </div>
                  <div className="mt-1 text-[11px] font-bold text-white/60">
                    {t.Engine?.missing ?? 'Missing'}
                  </div>
                </div>
              </div>

              {batchHit.found.length + batchHit.missing.length > 6 ? (
                <input
                  type="search"
                  className="rfid-search font-mono"
                  placeholder={t.Engine?.filterTags ?? 'Filter tags…'}
                  value={listFilter}
                  onChange={(e) => setListFilter(e.target.value)}
                  data-no-wedge="true"
                />
              ) : null}

              {filteredBatchFound.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    {t.Engine?.found ?? 'Found'} ({filteredBatchFound.length})
                  </div>
                  <div className="overflow-hidden rounded-[14px] border border-border bg-card">
                    <ul className="m-0 list-none p-0">
                      {filteredBatchFound.map((row, index) => (
                        <li
                          key={row.rfid_code}
                          className="flex items-center gap-3 px-3.5 py-3"
                          style={{
                            borderTop: index === 0 ? undefined : '1px solid var(--border)',
                          }}
                        >
                          <CheckCircle2
                            className="h-4 w-4 shrink-0 text-emerald-600"
                            strokeWidth={2.25}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="break-all font-mono text-[12px] font-bold text-foreground">
                              {row.rfid_code}
                            </div>
                            <div className="mt-1.5 flex flex-wrap items-center gap-2">
                              <StatusBadge status={row.status} />
                              <span
                                className={cn(
                                  'inline-flex max-w-full items-center rounded-lg px-2 py-0.5 font-mono text-[11px] font-extrabold',
                                  row.sku_code
                                    ? 'text-[color:var(--leap-brand)]'
                                    : 'text-amber-800'
                                )}
                                style={
                                  row.sku_code
                                    ? { background: `${BRAND}12` }
                                    : { background: '#fef3c7' }
                                }
                              >
                                {row.sku_code
                                  ? `${resultLabels.sku}: ${row.sku_code}`
                                  : resultLabels.unmapped}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                            onClick={() => copyText(row.rfid_code, resultLabels.copied)}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : null}

              {filteredBatchMissing.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-amber-700">
                    {t.Engine?.missing ?? 'Missing'} ({filteredBatchMissing.length})
                  </div>
                  <div className="overflow-hidden rounded-[14px] border border-amber-200 bg-amber-50/80">
                    <ul className="m-0 list-none p-0">
                      {filteredBatchMissing.map((code, index) => (
                        <li
                          key={code}
                          className="flex items-center gap-3 px-3.5 py-3"
                          style={{
                            borderTop: index === 0 ? undefined : '1px solid rgba(217,119,6,0.2)',
                          }}
                        >
                          <XCircle className="h-4 w-4 shrink-0 text-amber-700" strokeWidth={2.25} />
                          <span className="min-w-0 flex-1 break-all font-mono text-[12px] font-bold text-amber-950">
                            {code}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : null}

              {filteredBatchFound.length === 0 && filteredBatchMissing.length === 0 ? (
                <p className="m-0 rounded-2xl border border-border bg-card px-4 py-6 text-center text-xs font-semibold text-muted-foreground">
                  {t.Engine?.noSearchMatch ?? 'No tags match this search'}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </PageContainer>
  )
}
